import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MovementDetailDialog } from "../components/MovementDetail";
import { Container, ArrowDownToLine, ArrowUpFromLine, Warehouse, PlusCircle, Activity } from "lucide-react";
import { api, MOVEMENT_LABELS, MOVEMENT_COLORS, fmtDateTime, locationLabel } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

function MetricCard({ title, value, icon: Icon, tone, testid }) {
  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
      <CardContent className="p-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</p>
          <p className="font-outfit text-3xl font-extrabold mt-1" data-testid={`${testid}-value`}>{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tone}`}>
          <Icon className="w-5 h-5" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [selectedId, setSelectedId] = useState(null);
  const { data } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
    refetchInterval: 8000,
  });

  const stats = data || { active_containers: 0, movements_today: 0, on_ramp: 0, off_ramp: 0, by_type: {}, recent_movements: [], active_list: [] };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight">Panel en Tiempo Real</h1>
          <p className="text-sm text-slate-500 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rampgreen pulse-dot inline-block" /> Actualización automática cada 8 segundos
          </p>
        </div>
        <Link to="/registro" data-testid="dashboard-new-movement-link">
          <span className="inline-flex items-center gap-2 bg-navy hover:bg-navy-800 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors">
            <PlusCircle className="w-4 h-4" /> Nuevo Movimiento
          </span>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <MetricCard title="Contenedores Activos" value={stats.active_containers} icon={Container} tone="bg-navy text-telemetry" testid="metric-active-containers" />
        <MetricCard title="Movimientos Hoy" value={stats.movements_today} icon={Activity} tone="bg-cyan-100 text-cyan-700" testid="metric-movements-today" />
        <MetricCard title="En Rampa" value={stats.on_ramp} icon={Warehouse} tone="bg-emerald-100 text-emerald-700" testid="metric-on-ramp" />
        <MetricCard title="Fuera de Rampa" value={stats.off_ramp} icon={ArrowUpFromLine} tone="bg-amber-100 text-amber-700" testid="metric-off-ramp" />
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3 border-slate-200" data-testid="recent-movements-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-lg flex items-center gap-2"><Activity className="w-4 h-4 text-cyan-600" /> Movimientos Recientes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {stats.recent_movements.length === 0 && <p className="text-sm text-slate-400 py-6 text-center" data-testid="recent-empty">Sin movimientos registrados aún</p>}
            {stats.recent_movements.map((m) => (
              <button key={m.id} onClick={() => setSelectedId(m.id)}
                   className="w-full text-left flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2.5 hover:border-cyan-400 hover:bg-cyan-50/40 cursor-pointer transition-colors"
                   data-testid={`recent-movement-${m.movement_id}`}>
                <div className="min-w-0">
                  <span className="font-mono font-bold text-sm tracking-wider text-navy">{m.container_number}</span>
                  <p className="text-xs text-slate-500 truncate">{m.movement_id} · {m.operation_type} #{m.operation_number} · {fmtDateTime(m.created_at)}</p>
                </div>
                <Badge variant="outline" className={`${MOVEMENT_COLORS[m.movement_type]} whitespace-nowrap text-[11px]`}>
                  {MOVEMENT_LABELS[m.movement_type]}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-slate-200" data-testid="active-containers-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-lg flex items-center gap-2"><Container className="w-4 h-4 text-cyan-600" /> Contenedores en Sitio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[420px] overflow-y-auto">
            {stats.active_list.length === 0 && <p className="text-sm text-slate-400 py-6 text-center" data-testid="active-empty">No hay contenedores activos</p>}
            {stats.active_list.map((m) => (
              <button key={m.id} onClick={() => setSelectedId(m.id)}
                   className="w-full text-left rounded-lg border border-slate-100 px-3 py-2.5 space-y-1 hover:border-cyan-400 hover:bg-cyan-50/40 cursor-pointer transition-colors"
                   data-testid={`active-container-${m.container_number}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-sm tracking-wider">{m.container_number}</span>
                  <span className={`flex items-center gap-1.5 text-[11px] font-semibold ${m.fuera_de_rampa ? "text-amber-600" : "text-emerald-600"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full pulse-dot ${m.fuera_de_rampa ? "bg-amber-500" : "bg-emerald-500"}`} />
                    {locationLabel(m)}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{m.size} · {m.container_condition} · {m.truck_matricula || "Sin camión"}</p>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="today-breakdown-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-lg">Estadísticas del Día por Tipo</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(MOVEMENT_LABELS).map(([key, label]) => (
            <div key={key} className={`rounded-lg border px-4 py-3 ${MOVEMENT_COLORS[key]}`} data-testid={`today-type-${key}`}>
              <p className="text-xs font-semibold flex items-center gap-1">
                {key.includes("entrando") ? <ArrowDownToLine className="w-3.5 h-3.5" /> : <ArrowUpFromLine className="w-3.5 h-3.5" />}
                {label}
              </p>
              <p className="font-outfit text-2xl font-extrabold mt-1">{stats.by_type[key] || 0}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <MovementDetailDialog movementId={selectedId} open={!!selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
